/**
 * Маленькое хранилище «ключ — значение» в IndexedDB. Если браузер его не даёт (приватный режим, тесты),
 * данные живут в памяти до закрытия страницы: приложение работает, просто без запоминания.
 */
export interface Kv<T> {
  get(key: string): Promise<T | undefined>;
  set(key: string, value: T): Promise<void>;
  del(key: string): Promise<void>;
  all(): Promise<T[]>;
  clear(): Promise<void>;
}

const STORE = 'kv';

function memoryKv<T>(): Kv<T> {
  const map = new Map<string, T>();
  return {
    get: (key) => Promise.resolve(map.get(key)),
    set: (key, value) => {
      map.set(key, value);
      return Promise.resolve();
    },
    del: (key) => {
      map.delete(key);
      return Promise.resolve();
    },
    all: () => Promise.resolve([...map.values()]),
    clear: () => {
      map.clear();
      return Promise.resolve();
    },
  };
}

function openDb(name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('IndexedDB'));
    };
  });
}

function done<R>(req: IDBRequest<R>): Promise<R> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => {
      resolve(req.result);
    };
    req.onerror = () => {
      reject(req.error ?? new Error('IndexedDB'));
    };
  });
}

export function openKv<T>(name: string): Kv<T> {
  if (typeof indexedDB === 'undefined') return memoryKv<T>();
  let fallback: Kv<T> | null = null;
  let db: Promise<IDBDatabase> | null = null;
  const store = async (mode: IDBTransactionMode): Promise<IDBObjectStore> => {
    db ??= openDb(name);
    return (await db).transaction(STORE, mode).objectStore(STORE);
  };
  /** Не получилось с IndexedDB — дальше эта страница работает с памятью. */
  const guard = async <A>(attempt: () => Promise<A>, otherwise: (kv: Kv<T>) => Promise<A>): Promise<A> => {
    if (fallback !== null) return otherwise(fallback);
    try {
      return await attempt();
    } catch {
      fallback = memoryKv<T>();
      return otherwise(fallback);
    }
  };
  return {
    get: (key) =>
      guard(
        async () => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- IndexedDB отдаёт any; в хранилище лежит только T
          const req: IDBRequest<T | undefined> = (await store('readonly')).get(key);
          return done(req);
        },
        (k) => k.get(key),
      ),
    set: (key, value) =>
      guard(
        async () => {
          await done((await store('readwrite')).put(value, key));
        },
        (k) => k.set(key, value),
      ),
    del: (key) =>
      guard(
        async () => {
          await done((await store('readwrite')).delete(key));
        },
        (k) => k.del(key),
      ),
    all: () =>
      guard(
        async () => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- как выше
          const req: IDBRequest<T[]> = (await store('readonly')).getAll();
          return done(req);
        },
        (k) => k.all(),
      ),
    clear: () =>
      guard(
        async () => {
          await done((await store('readwrite')).clear());
        },
        (k) => k.clear(),
      ),
  };
}
